import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type CertificacionArtefacto = components['schemas']['SgsstCertificacionArtefacto']
export type Certificacion = components['schemas']['CertificacionRead']
export type CertificacionEstado = components['schemas']['CertificacionEstadoRead']
export type EstandarRespaldado = components['schemas']['EstandarRespaldadoRead']
export type CertificacionContenido = components['schemas']['CertificacionContenidoRead']

const ARTEFACTOS_CERTIFICABLES = [
  'matriz_riesgos',
  'matriz_legal',
  'plan_trabajo',
  'examenes',
  'indicadores',
] as const satisfies readonly CertificacionArtefacto[]

export function isCertificacionArtefacto(value: string | undefined): value is CertificacionArtefacto {
  return ARTEFACTOS_CERTIFICABLES.includes(value as CertificacionArtefacto)
}

export function useCertificacion(businessId: string | undefined, artefacto: CertificacionArtefacto) {
  return useQuery({
    queryKey: ['certificacion', businessId, artefacto],
    queryFn: async () =>
      (await api.get<CertificacionEstado>(`/businesses/${businessId}/sgsst/certificaciones/${artefacto}`)).data,
    enabled: !!businessId,
  })
}

// Contenido CONGELADO de la versión vigente: nunca se deriva del artefacto vivo.
export function useCertificacionContenido(businessId: string | undefined, artefacto: CertificacionArtefacto) {
  return useQuery({
    queryKey: ['certificacion-contenido', businessId, artefacto],
    queryFn: async () =>
      (await api.get<CertificacionContenido>(`/businesses/${businessId}/sgsst/certificaciones/${artefacto}/contenido`))
        .data,
    enabled: !!businessId,
    retry: false,
  })
}

export function useCertificar(businessId: string, artefacto: CertificacionArtefacto) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () =>
      (await api.post<Certificacion>(`/businesses/${businessId}/sgsst/certificaciones/${artefacto}/certificar`, {}))
        .data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['certificacion', businessId, artefacto]})
      // Los estándares sellados mueven las marcas de cumple del checklist.
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
    },
  })
}
